(function (window, document) {
  'use strict';

  if (!window || !document) {
    return;
  }

  /**
   * Load the YouTube iframe API so GTM/GA4 video tracking can attach to
   * click-to-play embeds. Players injected before the API is ready are
   * never adopted, so the API must load at page load rather than on click.
   *
   * @return {void}
   */
  function loadYouTubeIframeApi() {
    if (window.YT || document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    document.head.appendChild(script);
  }

  /**
   * Initialize inline video embeds.
   *
   * @return {void}
   */
  function initInlineVideos() {
    const triggers = document.querySelectorAll('[data-inline-video-trigger]:not([data-inline-video-initialized])');

    if (!triggers.length) {
      return;
    }

    loadYouTubeIframeApi();

    triggers.forEach(function (trigger) {
      trigger.setAttribute('data-inline-video-initialized', 'true');

      trigger.addEventListener('click', function (event) {
        event.preventDefault();

        const videoSrc = trigger.getAttribute('data-video-src');
        const videoTitle = trigger.getAttribute('data-video-title') || 'Video';

        if (!videoSrc) {
          return;
        }

        const wrapper = document.createElement('div');
        wrapper.className = trigger.className;
        wrapper.classList.remove('tw-cursor-pointer');

        const iframe = document.createElement('iframe');
        iframe.className = 'tw-h-full tw-w-full';
        iframe.src = videoSrc;
        iframe.title = videoTitle;
        iframe.loading = 'lazy';
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.allowFullscreen = true;

        wrapper.appendChild(iframe);
        trigger.parentNode.replaceChild(wrapper, trigger);
        iframe.focus();
      }, { once: true });
    });
  }

  /**
   * Initialize inline video transcript toggles.
   *
   * @return {void}
   */
  function initVideoTranscripts() {
    const toggles = document.querySelectorAll('[data-video-transcript-toggle]:not([data-video-transcript-initialized])');

    toggles.forEach(function (toggle) {
      const panelId = toggle.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;

      if (!panel) {
        return;
      }

      toggle.setAttribute('data-video-transcript-initialized', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;

      toggle.addEventListener('click', function () {
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

        toggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
        panel.hidden = isExpanded;
      });
    });
  }

  /**
   * Initialize inline quote carousels.
   *
   * @return {void}
   */
  function initInlineQuoteCarousels() {
    const carousels = document.querySelectorAll('[data-inline-quote-carousel]:not([data-inline-quote-carousel-initialized])');

    if (!carousels.length) {
      return;
    }

    carousels.forEach(function (carousel) {
      const slides = Array.from(carousel.querySelectorAll('[data-inline-quote-slide]'));
      const prev = carousel.querySelector('[data-inline-quote-prev]');
      const next = carousel.querySelector('[data-inline-quote-next]');
      let currentIndex = 0;

      if (!slides.length) {
        return;
      }

      carousel.setAttribute('data-inline-quote-carousel-initialized', 'true');

      /**
       * Set the active quote carousel slide.
       *
       * @param {number} index Target slide index.
       * @param {number} direction Animation direction.
       * @return {void}
       */
      function setCurrentSlide(index, direction) {
        const nextIndex = (index + slides.length) % slides.length;
        const currentSlide = slides[currentIndex];
        const nextSlide = slides[nextIndex];

        if (!nextSlide || nextIndex === currentIndex) {
          return;
        }

        const slideDirection = direction || (nextIndex > currentIndex ? 1 : -1);
        const fromX = slideDirection > 0 ? '56px' : '-56px';
        const toX = slideDirection > 0 ? '-56px' : '56px';

        if (currentSlide && currentSlide.animate) {
          currentSlide.animate([
            { opacity: 1, transform: 'translateX(0)' },
            { opacity: 0.88, transform: 'translateX(' + toX + ')' }
          ], { duration: 520, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
        }

        if (currentSlide) {
          currentSlide.classList.add('tw-hidden');
          currentSlide.setAttribute('aria-hidden', 'true');
        }

        currentIndex = nextIndex;

        nextSlide.classList.remove('tw-hidden');
        nextSlide.setAttribute('aria-hidden', 'false');

        if (nextSlide.animate) {
          nextSlide.animate([
            { opacity: 0.88, transform: 'translateX(' + fromX + ')' },
            { opacity: 1, transform: 'translateX(0)' }
          ], { duration: 560, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
        }
      }

      /**
       * Initialize quote carousel slide state.
       *
       * @return {void}
       */
      function initializeSlides() {
        currentIndex = 0;

        slides.forEach(function (slide, slideIndex) {
          const isActive = slideIndex === currentIndex;

          slide.classList.toggle('tw-hidden', !isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
      }

      if (prev) {
        prev.addEventListener('click', function () {
          setCurrentSlide(currentIndex - 1, -1);
        });
      }

      if (next) {
        next.addEventListener('click', function () {
          setCurrentSlide(currentIndex + 1, 1);
        });
      }

      carousel.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') {
          setCurrentSlide(currentIndex - 1, -1);
        }

        if (event.key === 'ArrowRight') {
          setCurrentSlide(currentIndex + 1, 1);
        }
      });

      initializeSlides();
    });
  }

  /**
   * Initialize v2 video carousels: one active slide, a strip of the others.
   *
   * @return {void}
   */
  function initVideoCarousels() {
    const carousels = document.querySelectorAll('[data-video-carousel]:not([data-video-carousel-initialized])');
    const desktop = window.matchMedia('(min-width: 1024px)');

    if (!carousels.length) {
      return;
    }

    loadYouTubeIframeApi();

    carousels.forEach(function (carousel) {
      const slides = Array.from(carousel.querySelectorAll('[data-video-carousel-slide]'));
      const items = Array.from(carousel.querySelectorAll('[data-video-carousel-item]'));
      const prev = carousel.querySelector('[data-video-carousel-prev]');
      const next = carousel.querySelector('[data-video-carousel-next]');
      let currentIndex = 0;

      if (slides.length < 2) {
        return;
      }

      carousel.setAttribute('data-video-carousel-initialized', 'true');

      /**
       * Replace the active slide's player with the YouTube iframe.
       *
       * @param {HTMLElement} trigger The play link inside the player.
       * @return {void}
       */
      function playVideo(trigger) {
        const player = trigger.closest('[data-video-carousel-player]');
        const iframe = document.createElement('iframe');

        iframe.src = trigger.getAttribute('data-video-src');
        iframe.title = trigger.getAttribute('data-video-title') || '';
        iframe.className = 'tw-absolute tw-inset-0 tw-h-full tw-w-full tw-border-0';
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.allowFullscreen = true;

        trigger.hidden = true;
        player.appendChild(iframe);
        iframe.focus();
      }

      /**
       * Stop and remove a slide's iframe so hidden slides never keep playing.
       *
       * @param {HTMLElement} slide The slide to reset.
       * @return {void}
       */
      function resetSlide(slide) {
        const iframe = slide.querySelector('[data-video-carousel-player] iframe');
        const trigger = slide.querySelector('[data-video-carousel-play]');

        if (iframe) {
          iframe.remove();
        }

        if (trigger) {
          trigger.hidden = false;
        }
      }

      /**
       * Show the active slide and the videos that follow it in the strip.
       *
       * @param {number} index Target slide index.
       * @return {void}
       */
      function setCurrentSlide(index) {
        const visibleItems = desktop.matches ? 2 : 1;

        currentIndex = (index + slides.length) % slides.length;

        slides.forEach(function (slide, slideIndex) {
          const isActive = slideIndex === currentIndex;

          if (!isActive) {
            resetSlide(slide);
          }

          slide.classList.toggle('tw-hidden', !isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        items.forEach(function (item, itemIndex) {
          const offset = (itemIndex - currentIndex + slides.length) % slides.length;
          const isVisible = offset > 0 && offset <= visibleItems;

          item.classList.toggle('tw-hidden', !isVisible);
          item.classList.toggle('tw-flex', isVisible);
        });
      }

      carousel.addEventListener('click', function (event) {
        const play = event.target.closest('[data-video-carousel-play]');
        const item = event.target.closest('[data-video-carousel-item]');

        if (play && carousel.contains(play)) {
          event.preventDefault();
          playVideo(play);
          return;
        }

        if (item && carousel.contains(item)) {
          setCurrentSlide(parseInt(item.getAttribute('data-video-carousel-item'), 10));
        }
      });

      if (prev) {
        prev.addEventListener('click', function () {
          setCurrentSlide(currentIndex - 1);
        });
      }

      if (next) {
        next.addEventListener('click', function () {
          setCurrentSlide(currentIndex + 1);
        });
      }

      desktop.addEventListener('change', function () {
        setCurrentSlide(currentIndex);
      });

      setCurrentSlide(0);
    });
  }

  /**
   * Initialize Backpage jump-to navigation.
   *
   * @return {void}
   */
  function initBackpageJumpTo() {
    const jumpTo = document.querySelector('[data-backpage-jump-to]:not([data-backpage-jump-to-initialized])');

    if (!jumpTo) {
      return;
    }

    jumpTo.setAttribute('data-backpage-jump-to-initialized', 'true');

    const toggle = jumpTo.querySelector('[data-backpage-jump-toggle]');
    const menu = jumpTo.querySelector('[data-backpage-jump-menu]');
    const toggleIcon = jumpTo.querySelector('[data-backpage-jump-toggle-icon]');

    /**
     * Set the Backpage jump-to menu open state.
     *
     * @param {boolean} isOpen Whether the menu should be open.
     * @return {void}
     */
    function setJumpToMenuOpen(isOpen) {
      if (!toggle || !menu) {
        return;
      }

      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      menu.classList.toggle('tw-pointer-events-none', !isOpen);
      menu.classList.toggle('tw-pointer-events-auto', isOpen);
      menu.classList.toggle('tw-max-h-0', !isOpen);
      menu.classList.toggle('tw-max-h-[50vh]', isOpen);
      menu.classList.toggle('tw-overflow-hidden', !isOpen);
      menu.classList.toggle('tw-overflow-y-auto', isOpen);
      menu.classList.toggle('tw-opacity-0', !isOpen);
      menu.classList.toggle('tw-opacity-100', isOpen);

      if (toggleIcon) {
        toggleIcon.classList.toggle('tw-rotate-180', isOpen);
      }
    }

    if (toggle && menu) {
      toggle.addEventListener('click', function () {
        setJumpToMenuOpen(toggle.getAttribute('aria-expanded') !== 'true');
      });

      jumpTo.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          setJumpToMenuOpen(false);
          toggle.focus();
        }
      });
    }

    const list = jumpTo.querySelector('ul');

    /**
     * Mark which ends of a horizontally scrolling jump list have hidden links.
     *
     * @return {void}
     */
    function updateListFade() {
      const maxScroll = list.scrollWidth - list.clientWidth;

      list.toggleAttribute('data-fade-start', maxScroll > 1 && list.scrollLeft > 1);
      list.toggleAttribute('data-fade-end', maxScroll > 1 && list.scrollLeft < maxScroll - 1);
    }

    /**
     * Turn a vertical wheel over an overflowing jump list into horizontal scroll, until it reaches an end.
     *
     * @param {WheelEvent} event Wheel event.
     * @return {void}
     */
    function scrollListWithWheel(event) {
      const maxScroll = list.scrollWidth - list.clientWidth;
      const isVertical = Math.abs(event.deltaY) > Math.abs(event.deltaX);
      const isAtEdge = (event.deltaY < 0 && list.scrollLeft <= 0) || (event.deltaY > 0 && list.scrollLeft >= maxScroll - 1);

      if (maxScroll <= 1 || !isVertical || isAtEdge) {
        return;
      }

      event.preventDefault();
      list.scrollLeft += event.deltaY;
    }

    if (list) {
      list.addEventListener('scroll', updateListFade, { passive: true });
      list.addEventListener('wheel', scrollListWithWheel, { passive: false });
      window.addEventListener('resize', updateListFade);
      updateListFade();

      if (document.fonts) {
        document.fonts.ready.then(updateListFade);
      }
    }

    const content = document.querySelector('.backpage-content');
    const links = Array.from(jumpTo.querySelectorAll('[data-backpage-jump-link]'));

    links.forEach(function (link) {
      link.addEventListener('click', function () {
        setJumpToMenuOpen(false);
      });
    });

    if (!content) {
      return;
    }

    const headings = Array.from(content.querySelectorAll('h2:not(.no-jump)'));

    if (!links.length || !headings.length) {
      return;
    }

    links.forEach(function (link, index) {
      const heading = headings[index];

      if (!heading) {
        return;
      }

      if (heading.id) {
        link.setAttribute('href', '#' + heading.id);
        return;
      }

      const hash = link.getAttribute('href') || '';
      const id = hash.charAt(0) === '#' ? hash.slice(1) : '';

      if (id) {
        heading.id = decodeURIComponent(id);
      }
    });
  }

  /**
   * Initialize related pages cards that collapse overflow links.
   *
   * @return {void}
   */
  function initRelatedPagesCards() {
    const cards = document.querySelectorAll('[data-related-pages-card]:not([data-related-pages-card-initialized])');

    if (!cards.length) {
      return;
    }

    cards.forEach(function (card) {
      const toggle = card.querySelector('[data-related-pages-toggle]');
      const label = card.querySelector('[data-related-pages-toggle-label]');
      const extraItems = Array.from(card.querySelectorAll('[data-related-pages-extra]'));
      const showMoreLabel = toggle ? (toggle.getAttribute('data-show-more-label') || 'Show More') : 'Show More';
      const showLessLabel = toggle ? (toggle.getAttribute('data-show-less-label') || 'Show Less') : 'Show Less';

      if (!toggle || !label || !extraItems.length) {
        return;
      }

      card.setAttribute('data-related-pages-card-initialized', 'true');

      /**
       * Toggle the expanded state for a related pages card.
       *
       * @param {boolean} isExpanded Whether the hidden links should be shown.
       * @return {void}
       */
      function setExpanded(isExpanded) {
        toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        label.textContent = isExpanded ? showLessLabel : showMoreLabel;

        extraItems.forEach(function (item) {
          item.classList.toggle('tw-hidden', !isExpanded);
        });
      }

      setExpanded(false);

      toggle.addEventListener('click', function () {
        setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
      });
    });
  }

  /**
   * Initialize "Read More" toggles that swap a trimmed text for the full one.
   *
   * @return {void}
   */
  function initReadMore() {
    const blocks = document.querySelectorAll('[data-read-more]:not([data-read-more-initialized])');

    blocks.forEach(function (block) {
      const toggle = block.querySelector('[data-read-more-toggle]');
      const shortText = block.querySelector('[data-read-more-short]');
      const fullText = block.querySelector('[data-read-more-full]');

      if (!toggle || !shortText || !fullText) {
        return;
      }

      block.setAttribute('data-read-more-initialized', 'true');

      const moreLabel = toggle.textContent;
      const lessLabel = toggle.getAttribute('data-label-less') || moreLabel;

      toggle.addEventListener('click', function () {
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

        toggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
        toggle.textContent = isExpanded ? moreLabel : lessLabel;
        shortText.hidden = !isExpanded;
        fullText.hidden = isExpanded;
      });
    });
  }

  /**
   * Show [data-reveal-at] elements only once the page has scrolled past their threshold (0-1).
   *
   * @return {void}
   */
  function initScrollReveal() {
    const elements = Array.from(document.querySelectorAll('[data-reveal-at]:not([data-reveal-initialized])'));

    if (!elements.length) {
      return;
    }

    let isQueued = false;

    /**
     * Toggle each element against the current scroll progress.
     *
     * @return {void}
     */
    function update() {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollable > 0 ? window.scrollY / scrollable : 1;

      elements.forEach(function (element) {
        const shouldShow = progress >= parseFloat(element.getAttribute('data-reveal-at'));

        if (shouldShow === !element.hidden) {
          return;
        }

        element.hidden = !shouldShow;
        element.classList.toggle('tw-animate-form-step-enter', shouldShow);
      });

      isQueued = false;
    }

    elements.forEach(function (element) {
      element.setAttribute('data-reveal-initialized', 'true');
    });

    window.addEventListener('scroll', function () {
      if (!isQueued) {
        isQueued = true;
        window.requestAnimationFrame(update);
      }
    }, { passive: true });

    window.addEventListener('resize', update);
    update();
  }

  /**
   * Open a modal [data-dialog] from links pointing at it, keeping the link's href as the no-JS fallback.
   *
   * @return {void}
   */
  function initDialogs() {
    const triggers = document.querySelectorAll('[data-dialog-open]:not([data-dialog-initialized])');
    const closeButtons = document.querySelectorAll('[data-dialog] [data-dialog-close]:not([data-dialog-initialized])');

    triggers.forEach(function (trigger) {
      const dialog = document.getElementById(trigger.getAttribute('data-dialog-open'));

      if (!dialog || typeof dialog.showModal !== 'function') {
        return;
      }

      trigger.setAttribute('data-dialog-initialized', 'true');
      trigger.addEventListener('click', function (event) {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
          return;
        }

        event.preventDefault();
        dialog.showModal();
      });
    });

    closeButtons.forEach(function (button) {
      button.setAttribute('data-dialog-initialized', 'true');
      button.addEventListener('click', function () {
        button.closest('[data-dialog]').close();
      });
    });
  }

  initInlineVideos();
  initVideoTranscripts();
  initInlineQuoteCarousels();
  initVideoCarousels();
  initBackpageJumpTo();
  initRelatedPagesCards();
  initReadMore();
  initScrollReveal();
  initDialogs();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initInlineVideos();
      initVideoTranscripts();
      initInlineQuoteCarousels();
  initVideoCarousels();
      initBackpageJumpTo();
      initRelatedPagesCards();
      initReadMore();
      initScrollReveal();
      initDialogs();
    }, { once: true });
  }
})(window, document);
